import React, { createContext, useContext, useState } from 'react';

import { Folder, TestCaseInFolder } from '@qality/common-model';

import AddToFolderModal from '../AddToFolderModal';
import CreateFolderModal from '../CreateFolderModal';
import DeleteFolderModal from '../DeleteFolderModal/DeleteFolderModal';
import DeleteFromFolderModal from '../DeleteFromFolderModal/DeleteFromFolderModal';
import { useTestCaseRepositoryFoldersContext } from '../TestCaseRepositoryFoldersProvider';

export type TestCaseRepositoryModalsContextValue = {
  openAddToFolderModal: (newAddToFolderModalProps: AddToFolderProps) => void;
  closeAddToFolderModal: () => void;
  openCreateFolderModal: (
    newCreateFolderProps?: Partial<CreateFolderProps>
  ) => void;
  closeCreateFolderModal: () => void;
  openDeleteFolderModal: (deleteProps: DeleteFolderProps) => void;
  closeDeleteFolderModal: () => void;
  openDeleteFromFolderModal: (
    deleteFromFolderProps: DeleteFromFolderProps
  ) => void;
  closeDeleteFromFolderModal: () => void;
};

export const initialContextValue: TestCaseRepositoryModalsContextValue = {
  openAddToFolderModal: () => undefined,
  closeAddToFolderModal: () => undefined,
  openCreateFolderModal: () => undefined,
  closeCreateFolderModal: () => undefined,
  openDeleteFolderModal: () => undefined,
  closeDeleteFolderModal: () => undefined,
  openDeleteFromFolderModal: () => undefined,
  closeDeleteFromFolderModal: () => undefined,
};

export const TestCaseRepositoryModalsContext =
  createContext<TestCaseRepositoryModalsContextValue>(initialContextValue);

export const useTestCaseRepositoryModalsContext =
  (): TestCaseRepositoryModalsContextValue => {
    return useContext(TestCaseRepositoryModalsContext);
  };

type Props = { children: React.ReactNode };

export type AddToFolderProps = {
  testCasesIds: Array<number | string>;
};

export type DeleteFromFolderProps = {
  testCasesInFolder: Array<TestCaseInFolder>;
  folder: Folder;
};

export type CreateFolderProps = {
  testCasesIds: Array<number | string>;
  parentFolder?: Folder | null;
};

export type DeleteFolderProps = {
  folder: Folder;
};

const defaultAddToFolderProps: AddToFolderProps = {
  testCasesIds: [],
};

const defaultDeleteFromFolderProps: DeleteFromFolderProps = {
  testCasesInFolder: [],
  folder: null,
};

const defaultCreateFolderProps: CreateFolderProps = {
  testCasesIds: [],
  parentFolder: null,
};

const defaultDeleteFolderProps: DeleteFolderProps = {
  folder: null,
};

export default function TestCaseRepositoryModalsProvider({
  children,
}: Props): JSX.Element {
  const {
    folders,
    addToFolder,
    deleteFromFolder,
    deleteFolder,
    addFolder,
    getFolderById,
    fetchFolderById,
    openFolder,
    getSubfolders,
  } = useTestCaseRepositoryFoldersContext();

  const [isAddToFolderModalOpen, setIsAddToFolderModalOpen] = useState(false);
  const [addToFolderProps, setAddToFolderProps] = useState<AddToFolderProps>(
    defaultAddToFolderProps
  );

  const [isDeleteFromFolderModalOpen, setIsDeleteFromFolderModalOpen] =
    useState(false);
  const [deleteFromFolderProps, setDeleteFromFolderProps] =
    useState<DeleteFromFolderProps>(defaultDeleteFromFolderProps);

  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [createFolderProps, setCreateFolderProps] = useState<CreateFolderProps>(
    defaultCreateFolderProps
  );

  const [isDeleteFolderModalOpen, setIsDeleteFolderModalOpen] = useState(false);
  const [deleteFolderProps, setDeleteFolderProps] = useState<DeleteFolderProps>(
    defaultDeleteFolderProps
  );

  const openAddToFolderModal = (newAddToFolderModalProps: AddToFolderProps) => {
    setIsAddToFolderModalOpen(true);
    setAddToFolderProps(newAddToFolderModalProps);
  };

  const closeAddToFolderModal = () => {
    setIsAddToFolderModalOpen(false);
    setAddToFolderProps(defaultAddToFolderProps);
  };

  const handleAddToFolder = (
    folderId: Folder['id'],
    testCasesInFolder: Array<TestCaseInFolder>
  ) => {
    addToFolder(folderId, testCasesInFolder);
    closeAddToFolderModal();
  };

  const openDeleteFromFolderModal = (
    deleteFromFolderModalProps: DeleteFromFolderProps
  ) => {
    setIsDeleteFromFolderModalOpen(true);
    setDeleteFromFolderProps(deleteFromFolderModalProps);
  };

  const closeDeleteFromFolderModal = () => {
    setIsDeleteFromFolderModalOpen(false);
    setDeleteFromFolderProps(defaultDeleteFromFolderProps);
  };

  const handleDeleteFromFolder = (
    folderId: Folder['id'],
    testCasesInFolder: Array<TestCaseInFolder>
  ) => {
    deleteFromFolder(folderId, testCasesInFolder);
    closeDeleteFromFolderModal();
  };

  const openCreateFolderModal = (
    newCreateFolderProps?: Partial<CreateFolderProps>
  ) => {
    setCreateFolderProps({
      ...defaultCreateFolderProps,
      ...newCreateFolderProps,
    });
    setIsCreateFolderModalOpen(true);
  };

  const closeCreateFolderModal = () => {
    setIsCreateFolderModalOpen(false);
  };

  const openDeleteFolderModal = (deleteProps: DeleteFolderProps) => {
    setIsDeleteFolderModalOpen(true);
    setDeleteFolderProps(deleteProps);
  };

  const closeDeleteFolderModal = () => {
    setIsDeleteFolderModalOpen(false);
  };

  const handleDeleteFolder = () => {
    const { folder } = deleteFolderProps;

    deleteFolder(folder);

    if (folder.parentId) {
      const parentFolder = getFolderById(folder.parentId);
      openFolder(parentFolder);
    }

    closeDeleteFolderModal();
  };

  return (
    <TestCaseRepositoryModalsContext.Provider
      value={{
        openCreateFolderModal,
        closeCreateFolderModal,
        openAddToFolderModal,
        closeAddToFolderModal,
        openDeleteFolderModal,
        closeDeleteFolderModal,
        openDeleteFromFolderModal,
        closeDeleteFromFolderModal,
      }}
    >
      {children}
      {isAddToFolderModalOpen && (
        <AddToFolderModal
          onAddToFolder={handleAddToFolder}
          closeDialog={closeAddToFolderModal}
          testCasesIds={addToFolderProps.testCasesIds}
          folders={folders}
          getSubfolders={getSubfolders}
          fetchFolderById={fetchFolderById}
        />
      )}
      {isDeleteFromFolderModalOpen && (
        <DeleteFromFolderModal
          onDeleteFromFolder={handleDeleteFromFolder}
          onClose={closeDeleteFromFolderModal}
          testCasesInFolder={deleteFromFolderProps.testCasesInFolder}
          folder={deleteFromFolderProps.folder}
        />
      )}
      {isCreateFolderModalOpen && (
        <CreateFolderModal
          onCreateFolder={addFolder}
          onClose={closeCreateFolderModal}
          createFolderProps={createFolderProps}
        />
      )}
      {isDeleteFolderModalOpen && (
        <DeleteFolderModal
          onDeleteFolder={handleDeleteFolder}
          onClose={closeDeleteFolderModal}
          deleteFolderProps={deleteFolderProps}
        />
      )}
    </TestCaseRepositoryModalsContext.Provider>
  );
}
